<script>
import { defineAsyncComponent } from 'vue';

// components
import { StoriesComponent } from '@rei/landing-story';
import FilmstripCarousel from '@rei/filmstrip-carousel';
import LandingBanner from '@rei/banner-component';
import { LandingContentBlock, LandingAction } from '@rei/landing-content-block';
import LandingImageComponent from '@rei/landing-image-component';
import { IconPlayFill } from '@rei/cedar';
import LeadComponent from '../../../common/components/lead/LeadComponent.vue';
import StoryVideoComponent from '../../../common/components/story-video-component/StoryVideoComponent.vue';
import UgcPlacementComponent from '../../../common/components/ugc-placement-component/UgcPlacementComponent.vue';
import QuadGridCategories from '../../event/components/quad-grid/QuadGridCategories.vue';

// mixins and utils
import analytics from '../../../common/mixins/analytics';
import ugcPlacementData from '../../../common/mixins/ugcPlacementData';

export default {
  name: 'OptOutside',
  components: {
    LeadComponent,
    StoriesComponent,
    FilmstripCarousel,
    QuadGridCategories,
    UgcPlacementComponent,
    LandingBanner,
    LandingContentBlock,
    LandingAction,
    LandingImageComponent,
    YoutubeVideoModal: defineAsyncComponent(() => import(
      '../../../common/components/youtube-video-modal/YoutubeVideoModal.vue'
    )),
    IconPlayFill,
    StoryVideoComponent,
  },
  mixins: [analytics, ugcPlacementData],
  props: {
    additionalData: { type: Object, required: true, default: () => {} },
    componentModels: { type: Object, required: true, default: () => {} },
  },
  data() {
    return {
      graphQLEnabled: true, // This is needed for ugcPlacementData to work.
      isYoutubeVideoModalOpen: {
        firstStory: false,
        outro: false,
      },
      isMounted: false,
    };
  },
  computed: {
    renderingMap() {
      return {
        lead: !!this.lead,
        'story-pack': !!this.storyPack,
        outro: !!this.outro,
        'filmstrip-one': !!this.filmstripOne,
        'story-sms': !!this.storySms,
        'quad-grid': !!this.quadGrid,
        'filmstrip-two': !!this.filmstripTwo,
        'ugc-gallery':
          !!this.ugcPlacementModule
          && !!this.ugcGallery
          && !!this.ugcModuleEnabled,
      };
    },
    lead() {
      const { lead } = this.componentModels || {};
      return (!!lead && lead) || null;
    },
    storyPack() {
      const { storyPack } = this.componentModels || {};
      return (!!storyPack && storyPack) || null;
    },
    filmstripOne() {
      const { filmstripOne } = this.componentModels || {};
      return (!!filmstripOne && filmstripOne) || null;
    },
    quadGrid() {
      const { quadGrid } = this.componentModels || {};
      return (!!quadGrid && quadGrid) || null;
    },
    filmstripTwo() {
      const { filmstripTwo } = this.componentModels || {};
      return (!!filmstripTwo && filmstripTwo) || null;
    },
    ugcGallery() {
      const { ugcGallery } = this.componentModels || {};
      return (!!ugcGallery && ugcGallery) || null;
    },
    outro() {
      const { outro } = this.componentModels || {};
      return (!!outro && outro) || null;
    },
    storySms() {
      const { storySms } = this.componentModels || {};
      return (!!storySms && storySms) || null;
    },
  },
  mounted() {
    this.isMounted = true;
  },
  methods: {
    openModalOutro() {
      this.isYoutubeVideoModalOpen = { ...this.isYoutubeVideoModalOpen, outro: true };
    },
    closeModalOutro(modalStatus) {
      this.isYoutubeVideoModalOpen = { ...this.isYoutubeVideoModalOpen, outro: modalStatus };
    },
    openModalStory() {
      this.isYoutubeVideoModalOpen = { ...this.isYoutubeVideoModalOpen, firstStory: true };
    },
    closeModalStory(modalStatus) {
      this.isYoutubeVideoModalOpen = { ...this.isYoutubeVideoModalOpen, firstStory: modalStatus };
    },
  },
};
</script>
<template>
  <div
    id="opt-outside"
    class="opt-outside"
    data-ui="opt-outside"
  >
    <section
      v-if="renderingMap['lead']"
      :data-location-name="analyticsMap['lead']"
      data-ui="lead"
    >
      <lead-component v-bind="lead.componentProps" />
    </section>
    <div class="opt-outside__region opt-outside__region--dark">
      <section
        v-if="renderingMap['story-pack']"
        :data-location-name="analyticsMap['story-pack']"
        data-ui="story-pack"
        class="opt-outside__section opt-outside__section--constrained"
      >
        <story-video-component
          v-bind="storyPack.componentProps"
          @open-youtube-video-modal="openModalStory"
        />
      </section>
    </div>

    <section
      v-if="renderingMap['outro']"
      :data-location-name="analyticsMap['outro']"
      data-ui="outro"
    >
      <landing-banner v-bind="outro.componentProps.bannerComponentProps">
        <template #content>
          <landing-content-block v-bind="outro.componentProps.content">
            <template #javascriptActions="{ javascriptActions }">
              <landing-action
                v-if="javascriptActions && javascriptActions[0]"
                v-bind="javascriptActions[0]"
                data-ui="open-modal-outro"
                @click="openModalOutro()"
              >
                <template #landing-action-icon>
                  <icon-play-fill />
                </template>
              </landing-action>
            </template>
          </landing-content-block>
        </template>
        <template #background>
          <landing-image-component v-bind="outro.componentProps.media" />
        </template>
      </landing-banner>
    </section>

    <div class="opt-outside__region opt-outside__region--light">
      <section
        v-if="renderingMap['filmstrip-one']"
        :data-location-name="analyticsMap['filmstrip-one']"
        data-ui="filmstrip-one"
        class="opt-outside__section opt-outside__section--constrained"
      >
        <filmstrip-carousel v-bind="filmstripOne.componentProps" />
      </section>

      <section
        v-if="renderingMap['story-sms']"
        :data-location-name="analyticsMap['story-sms']"
        class="opt-outside__section
        opt-outside__section--constrained"
        data-ui="story-sms"
      >
        <stories-component
          v-bind="storySms.componentProps"
          aspect-ratio="4/3"
          grid="single"
        />
      </section>
    </div>

    <div class="opt-outside__region opt-outside__region--dark">
      <section
        v-if="renderingMap['quad-grid']"
        class="opt-outside__section opt-outside__section--constrained"
        :data-location-name="analyticsMap['quad-grid']"
        data-ui="quad-grid"
      >
        <quad-grid-categories
          v-bind="quadGrid.componentProps"
        />
      </section>
      <section
        v-if="renderingMap['filmstrip-two']"
        :data-location-name="analyticsMap['filmstrip-two']"
        data-ui="filmstrip-two"
        class="opt-outside__section opt-outside__section--constrained"
      >
        <filmstrip-carousel v-bind="filmstripTwo.componentProps" />
      </section>
    </div>

    <div
      v-if="renderingMap['ugc-gallery']"
      class="opt-outside__region opt-outside__region--light"
    >
      <section
        :data-location-name="analyticsMap['ugc-gallery']"
        data-ui="ugc-gallery"
        class="opt-outside__section opt-outside__section--constrained opt-outside__section--ugc"
      >
        <ugc-placement-component
          v-bind="ugcPlacementModule"
          :config="ugcGallery.config"
          :content="ugcGallery.componentProps.content"
          :has-more-items="hasMoreItems.hasMore"
          @onLoadMore="loadMoreUgcData"
        />
      </section>
    </div>

    <youtube-video-modal
      v-if="storyPack?.componentProps.stories[0].config && isMounted"
      :data-location-name="analyticsMap['video-modal-first-story']"
      :opened="isYoutubeVideoModalOpen.firstStory"
      v-bind="storyPack?.componentProps.stories[0].config"
      @onModalClose="closeModalStory"
    />
  </div>
</template>
<style lang="scss" scoped>
  @import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';

.opt-outside {
  &__section {
    padding-top: $cdr-space-four-x;
    @include cdr-sm-mq-up {
      padding-bottom: $cdr-space-four-x;
    }

    &--constrained {
      @include cdr-container;
    }

    &--ugc {
      @include cdr-sm-mq-up {
        padding-bottom: 0;
      }
    }
  }

  &__region {
    padding-bottom: $cdr-space-four-x;

    &--dark {
      background: #121212;
    }

    &--light {
      background: $cdr-color-background-secondary;
      padding-bottom: $cdr-space-four-x;
    }

    &--light + &--dark {
      @include cdr-sm-mq-up {
        padding-top: $cdr-space-four-x;
      }
    }
  }

  :deep(.landing-stories__heading-title) {
    font-weight: 400 !important;
  }

  :deep(.landing-image-component__image) {
    max-width: 100%;
  }

  &__region{
    &--dark{
      :deep(.quad-grid__heading),
      :deep(.quad-grid__subheading),
      :deep(.quad-grid__featured-link),
      :deep(.simple-top-categories__link),
      :deep(.landing-stories__heading-title){
        color: #ffffff;
      }

      :deep(.simple-top-categories__link){
        text-align: center;
      }

      :deep(.quad-grid__heading){
        @include cdr-text-heading-serif-1000;
      }

      :deep(.quad-grid__subheading){
        @include cdr-text-body-300;
      }

      :deep(.quad-grid){
        margin-top: $cdr-space-one-and-a-half-x;
      }
    }
  }
}
</style>
